import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './icons';

// The prototype's overlay host (#overlay-root): one dialog or drawer at a time, backdrop click and Escape close it,
// focus is trapped inside and returned to whatever opened it. Bodies are React trees, so they can hold state.
const DialogContext = createContext({ openDialog: () => {}, openDrawer: () => {}, closeDialog: () => {}, isOpen: false });
export const useDialogs = () => useContext(DialogContext);

const FOCUSABLE = 'button:not(:disabled), input:not([type="checkbox"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]';
const visible = el => el.getClientRects().length > 0;

export function DialogProvider({ children }) {
  const [current, setCurrent] = useState(null);
  const lastFocus = useRef(null);
  const hostRef = useRef(null);

  const open = useCallback((kind, props) => {
    setCurrent(prev => {
      if (!prev) lastFocus.current = document.activeElement;
      return { kind, key: Date.now(), ...props };
    });
  }, []);
  const openDialog = useCallback(props => open('dialog', props), [open]);
  const openDrawer = useCallback(props => open('drawer', props), [open]);
  const closeDialog = useCallback(() => setCurrent(null), []);

  useEffect(() => {
    if (!current) return undefined;
    const host = hostRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusFirst = () => {
      const target = host?.querySelector(current.autoFocus || FOCUSABLE);
      target?.focus({ preventScroll: true });
    };
    const timer = setTimeout(focusFirst, 0);
    const onKey = e => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') { e.preventDefault(); setCurrent(null); return; }
      if (e.key !== 'Tab' || !host) return;
      const controls = [...host.querySelectorAll(FOCUSABLE)].filter(visible);
      if (!controls.length) return;
      const first = controls[0], last = controls[controls.length - 1];
      if (e.shiftKey && (document.activeElement === first || !host.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [current]);
  useEffect(() => {
    if (current) return;
    const el = lastFocus.current;
    if (el && el.isConnected && typeof el.focus === 'function') el.focus({ preventScroll: true });
    lastFocus.current = null;
  }, [current]);

  const value = useMemo(() => ({ openDialog, openDrawer, closeDialog, isOpen: !!current }), [openDialog, openDrawer, closeDialog, current]);
  return (
    <DialogContext.Provider value={value}>
      {children}
      <div id="overlay-root" className="sp-app trace" ref={hostRef}>
        {current && (current.kind === 'drawer' ? <DrawerOverlay {...current} onClose={closeDialog} /> : <DialogOverlay {...current} onClose={closeDialog} />)}
      </div>
    </DialogContext.Provider>
  );
}

function backdropClick(onClose) {
  return e => { if (e.target === e.currentTarget) onClose(); };
}

function DialogOverlay({ title, body, footer, large = false, command = false, label, onClose }) {
  if (command) {
    return (
      <div className="overlay" data-backdrop onMouseDown={backdropClick(onClose)}>
        <section className="dialog command-dialog" role="dialog" aria-modal="true" aria-label={label || title || 'Dialog'}>{body}</section>
      </div>
    );
  }
  return (
    <div className="overlay" data-backdrop onMouseDown={backdropClick(onClose)}>
      <section className={`dialog${large ? ' large' : ''}`} role="dialog" aria-modal="true" aria-label={label || title}>
        <header className="dialog-head"><h2>{title}</h2>
          <button type="button" className="btn ghost icon-btn" onClick={onClose} aria-label="Close dialog"><Icon name="close" /></button>
        </header>
        <div className="dialog-body">{body}</div>
        {footer && <footer className="dialog-footer">{footer}</footer>}
      </section>
    </div>
  );
}

function DrawerOverlay({ body, label = 'Details', onClose }) {
  return (
    <div className="overlay drawer-overlay" data-backdrop onMouseDown={backdropClick(onClose)}>
      <section className="drawer" role="dialog" aria-modal="true" aria-label={label}>{body}</section>
    </div>
  );
}

// The drawer's sticky head: an uppercase label and the close control, then whatever the screen adds.
export function DrawerHead({ label, closeLabel = 'Close details', children }) {
  const { closeDialog } = useDialogs();
  return (
    <header className="drawer-head">
      <div className="between"><span className="label">{label}</span>
        <button type="button" className="btn ghost icon-btn" onClick={closeDialog} aria-label={closeLabel}><Icon name="close" /></button>
      </div>
      {children}
    </header>
  );
}

// A close button for dialog footers.
export function CloseButton({ children = 'Close', primary = false, className = '' }) {
  const { closeDialog } = useDialogs();
  return <button type="button" className={`btn${primary ? ' primary' : ''}${className ? ` ${className}` : ''}`} onClick={closeDialog}>{children}</button>;
}
